import { getApiClient } from '@src/shared/types/api-client'
import { DevopsTicket } from '@src/tools/error-tracker/types/Tickets'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'tickets'
const LIST = 'list'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  list: () => [{ scope: SCOPE, entity: LIST }] as const,
}

async function getTickets(): Promise<DevopsTicket[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<DevopsTicket[]>('/Tickets')
  return response.data
}

interface CloseRelatedErrorsRequest {
  ticketIds: string[]
}

interface CloseRelatedErrorsResponse {
  totalErrorsClosed: number
  processedTicketIds: string[]
}

async function closeRelatedErrors(request: CloseRelatedErrorsRequest): Promise<CloseRelatedErrorsResponse> {
  const apiClient = getApiClient()
  const response = await apiClient.post<CloseRelatedErrorsResponse>('/Tickets/actions/close-errors', request)
  return response.data
}

export function useTickets(): [DevopsTicket[] | undefined, boolean, Error | null] {
  const { data, isLoading, error } = useQuery({
    queryKey: keysFactory.list(),
    queryFn: getTickets,
  })
  return [data, isLoading, error]
}

export function useCloseRelatedErrors(): [(request: CloseRelatedErrorsRequest) => Promise<CloseRelatedErrorsResponse>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: closeRelatedErrors,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.list() })
    },
  })
  return [mutateAsync, isPending]
}
